// Replaces the source's global STATE object (source line 3033). Now holds
// only session/navigation state - all domain data is fetched on demand from
// the microservices instead of living entirely in-memory.
import { ROLES, QUERY_MANAGE_ROLES, QUERY_ANSWER_ROLES, PREFERRED_TERM_WRITE_ROLES, type RoleDef } from "@woof/shared";

export interface CurrentUser {
  id: string;
  username: string;
  role: string;
  sites: string[];
  fullName: string;
  mustChangePassword: boolean;
}

export type ViewName = "studies" | "ecrf" | "dev" | "q" | "audit" | "admin";

export const STATE: {
  user: CurrentUser | null;
  view: ViewName;
  currentStudyId: number | null;
  currentPatientId: string | null;
  currentVisitKey: string | null;
  currentSection: string | null;
  patients: any[];
} = {
  user: null,
  view: "studies",
  currentStudyId: null,
  currentPatientId: null,
  currentVisitKey: null,
  currentSection: null,
  patients: [],
};

export function currentRole(): RoleDef | null {
  if (!STATE.user) return null;
  return ROLES[STATE.user.role] ?? null;
}

export function isAdmin(): boolean {
  return !!currentRole()?.perms.admin;
}

// Queries workflow separation of duties - see services/queries.
export function canManageQueries(): boolean {
  return !!STATE.user && QUERY_MANAGE_ROLES.includes(STATE.user.role);
}
export function canAnswerQuery(): boolean {
  return !!STATE.user && QUERY_ANSWER_ROLES.includes(STATE.user.role);
}

// Sponsor-authority fields (AE.aedecod, deviation sponsor-report) -
// same PREFERRED_TERM_WRITE_ROLES gate the backend enforces.
export function canReportToSponsor(): boolean {
  return !!STATE.user && PREFERRED_TERM_WRITE_ROLES.includes(STATE.user.role);
}

// Audit Trail is admin/sponsor-only - same PREFERRED_TERM_WRITE_ROLES set,
// mirroring the backend gate in services/audit/routes/auditRoutes.ts.
export function canViewAudit(): boolean {
  return !!STATE.user && PREFERRED_TERM_WRITE_ROLES.includes(STATE.user.role);
}

// Spreadsheet import (Administration page) is admin/sponsor-only, same
// PREFERRED_TERM_WRITE_ROLES set - mirrors authorizeImport() in
// services/clinical-data/src/authz.ts and services/safety/src/authz.ts.
export function canImportData(): boolean {
  return !!STATE.user && PREFERRED_TERM_WRITE_ROLES.includes(STATE.user.role);
}

// Notification settings (sponsor email) - same PREFERRED_TERM_WRITE_ROLES
// gate as canManageNotifications() in services/auth/src/routes/
// notificationSettingsRoutes.ts.
export function canEditNotificationSettings(): boolean {
  return !!STATE.user && PREFERRED_TERM_WRITE_ROLES.includes(STATE.user.role);
}

export function canReadSite(siteCode: string): boolean {
  const role = currentRole();
  if (!role || !STATE.user) return false;
  if (role.perms.read === "all") return true;
  if (role.perms.read === "site") return STATE.user.sites.includes(siteCode);
  return false;
}

export function canWriteSite(siteCode: string): boolean {
  const role = currentRole();
  if (!role || !STATE.user) return false;
  if (role.perms.write === "all") return true;
  if (role.perms.write === "site") return STATE.user.sites.includes(siteCode);
  return false;
}
